import EmptyState from 'components/EmptyState';
import { EllipsisIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import PageHeader from 'components/PageHeader';
import { DataTable, type DataTableColumnDef } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { FORM_LABEL, periodLabel } from 'pages/home/widgets/creator/catalogueLabels';
import { WidgetCreator } from 'pages/home/widgets/creator/WidgetCreator';
import { FilterPills } from 'pages/home/widgets/share/FilterPills';
import { SearchField } from 'pages/home/widgets/share/SearchField';
import { ShareWidget } from 'pages/home/widgets/share/ShareWidget';
import { DeleteWidgetDialog } from 'pages/home/widgets/WidgetMenu';
import { useMemo, useState } from 'react';
import { useGetFirmWidgetsQuery, useSetWidgetSharingMutation } from 'store/api/homeWidgetSharing';
import type { FirmWidget } from 'types/homeWidgetSharing';
import { foldForSearch } from 'utils/foldForSearch';
import { t } from 'utils/getTranslations';

type Show = 'ALL' | 'GALLERY' | 'HOMES';

const ITEM_CLASS = 'h-[34px] px-2.5';
const CHIP_CLASS = 'bg-neutral-tint text-meta flex h-[22px] items-center rounded-sm px-2 whitespace-nowrap';

/**
 * Every widget shared with the firm, for whoever holds the sharing privilege: in the gallery or not, on whose Home,
 * for how many people, by whom and when it last changed. The whole list is loaded at once, so the search and the
 * three pills narrow it in the browser. A widget is shared from its own menu on Home; here one is changed, taken
 * back or deleted.
 */
export function FirmWidgetsPage() {
    const { data: firm, isError, isLoading, refetch } = useGetFirmWidgetsQuery();
    const [search, setSearch] = useState('');
    const [show, setShow] = useState<Show>('ALL');

    const columns = useMemo<DataTableColumnDef<FirmWidget>[]>(
        () => [
            {
                id: 'widget',
                header: t('home.widgets.firm.column.widget'),
                enableSorting: false,
                meta: { widthWeight: 1.5, minPx: 200 },
                cell: ({ row }) => {
                    const { name, definition } = row.original.widget;
                    return (
                        <div className="flex min-w-0 flex-col">
                            <span className="truncate font-medium">{name}</span>
                            <span className="text-muted-foreground truncate text-xs">
                                {t(FORM_LABEL[definition.form])} · {periodLabel(definition.period)}
                            </span>
                        </div>
                    );
                },
            },
            {
                id: 'gallery',
                header: t('home.widgets.firm.column.gallery'),
                enableSorting: false,
                meta: { fixedPx: 96 },
                cell: ({ row }) =>
                    row.original.widget.sharing.inGallery ? (
                        t('yes')
                    ) : (
                        <span className="text-muted-foreground">{t('no')}</span>
                    ),
            },
            {
                id: 'homes',
                header: t('home.widgets.firm.column.homes'),
                enableSorting: false,
                meta: { widthWeight: 1.6, minPx: 200 },
                cell: ({ row }) => {
                    const { widget, everyone, offices, teams, employees } = row.original;
                    if (!widget.sharing.onHomes) {
                        return <span className="text-muted-foreground">{t('home.widgets.firm.nobody')}</span>;
                    }
                    return (
                        <div className="flex flex-wrap gap-1">
                            {everyone ? (
                                <span className={CHIP_CLASS}>{t('home.widgets.share.everyone')}</span>
                            ) : (
                                // An office, a team and a person can share an id, so the kind is part of the key.
                                [offices, teams, employees].flatMap((targets, kind) =>
                                    targets.map(({ id, name }) => (
                                        <span key={`${kind}-${id}`} className={CHIP_CLASS}>
                                            {name}
                                        </span>
                                    ))
                                )
                            )}
                        </div>
                    );
                },
            },
            {
                id: 'people',
                header: t('home.widgets.firm.column.people'),
                enableSorting: false,
                meta: { fixedPx: 92, textAlign: 'right' },
                cell: ({ row }) => {
                    const { onHomes, recipientCount } = row.original.widget.sharing;
                    return <span className="text-meta font-mono">{onHomes ? recipientCount : '—'}</span>;
                },
            },
            {
                id: 'sharedBy',
                header: t('home.widgets.firm.column.shared-by'),
                enableSorting: false,
                meta: { widthWeight: 1, minPx: 140 },
                cell: ({ row }) => row.original.sharedBy.name,
            },
            {
                id: 'changed',
                header: t('home.widgets.firm.column.changed'),
                enableSorting: false,
                meta: { fixedPx: 132 },
                cell: ({ row }) => <FormattedDate value={row.original.widget.lastModifiedAt} nowrap />,
            },
            {
                id: 'actions',
                header: '',
                enableSorting: false,
                // The 32px button and the cell's own 32px of padding.
                meta: { fixedPx: 64 },
                cell: ({ row }) => <FirmWidgetMenu row={row.original} />,
            },
        ],
        []
    );

    const rows = firm ?? [];
    const inGallery = rows.filter(({ widget }) => widget.sharing.inGallery);
    const onHomes = rows.filter(({ widget }) => widget.sharing.onHomes);
    const wanted = foldForSearch(search.trim());
    const shown = (show === 'GALLERY' ? inGallery : show === 'HOMES' ? onHomes : rows).filter(({ widget }) =>
        foldForSearch(widget.name).includes(wanted)
    );

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <PageHeader titleId="home.widgets.firm.title" showBreadcrumb />
                    <p className="text-foreground-secondary">{t('home.widgets.firm.about')}</p>
                </div>
                {isError && !firm ? (
                    <EmptyState
                        size="page"
                        variant="error"
                        errorTitleId="home.widgets.error.loading-firm"
                        onRetry={refetch}
                        surface="firm-widgets"
                    />
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-3">
                            <SearchField
                                label={t('home.widgets.firm.search')}
                                placeholder={t('home.widgets.firm.search-placeholder')}
                                value={search}
                                onChange={setSearch}
                                className="h-10"
                            />
                            <FilterPills
                                label={t('home.widgets.firm.show')}
                                value={show}
                                options={[
                                    {
                                        value: 'ALL',
                                        label: t('home.widgets.firm.show.all', { count: String(rows.length) }),
                                    },
                                    {
                                        value: 'GALLERY',
                                        label: t('home.widgets.firm.show.gallery', { count: String(inGallery.length) }),
                                    },
                                    {
                                        value: 'HOMES',
                                        label: t('home.widgets.firm.show.homes', { count: String(onHomes.length) }),
                                    },
                                ]}
                                onChange={setShow}
                            />
                        </div>
                        <DataTable<FirmWidget>
                            columns={columns}
                            data={shown}
                            getRowId={(row) => String(row.widget.id)}
                            columnConfigId="firm-widgets"
                            isLoading={isLoading}
                            emptyState={
                                rows.length === 0 ? (
                                    <EmptyState size="page" titleId="home.widgets.firm.empty" surface="firm-widgets" />
                                ) : (
                                    <EmptyState
                                        size="page"
                                        variant="filtered"
                                        onClearFilters={() => {
                                            setSearch('');
                                            setShow('ALL');
                                        }}
                                        surface="firm-widgets"
                                    />
                                )
                            }
                        />
                    </>
                )}
            </div>
        </PaddedLayout>
    );
}

/**
 * What can be done with one shared widget. Each way of sharing is taken back on its own, the other half being sent
 * back as it stands; with neither left the widget is private again and leaves the list.
 */
function FirmWidgetMenu({ row }: { row: FirmWidget }) {
    const [open, setOpen] = useState<'edit' | 'share' | 'delete' | null>(null);
    const [setSharing] = useSetWidgetSharingMutation();
    const { widget } = row;
    const { inGallery, onHomes } = widget.sharing;
    const gallery = { inGallery, description: row.description, subject: row.subject };
    const close = () => setOpen(null);

    return (
        <div className="flex justify-end">
            <DropdownMenu>
                <DropdownMenuTrigger
                    render={
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('home.widgets.menu.label', { name: widget.name })}
                            className="text-foreground-secondary"
                        />
                    }
                >
                    <EllipsisIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[250px]">
                    <DropdownMenuItem className={ITEM_CLASS} onClick={() => setOpen('edit')}>
                        {t('home.widgets.firm.edit')}
                    </DropdownMenuItem>
                    <DropdownMenuItem className={ITEM_CLASS} onClick={() => setOpen('share')}>
                        {t('home.widgets.firm.change')}
                    </DropdownMenuItem>
                    {onHomes ? (
                        <DropdownMenuItem
                            className={ITEM_CLASS}
                            onClick={() => setSharing({ id: widget.id, ...gallery, onHomes: false })}
                        >
                            {t('home.widgets.firm.take-off')}
                        </DropdownMenuItem>
                    ) : null}
                    {inGallery ? (
                        <DropdownMenuItem
                            className={ITEM_CLASS}
                            onClick={() =>
                                setSharing({
                                    id: widget.id,
                                    inGallery: false,
                                    onHomes,
                                    everyone: row.everyone,
                                    officeIds: row.offices.map((office) => office.id),
                                    teamIds: row.teams.map((team) => team.id),
                                    employeeIds: row.employees.map((employee) => employee.id),
                                })
                            }
                        >
                            {t('home.widgets.firm.remove-from-gallery')}
                        </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" className={ITEM_CLASS} onClick={() => setOpen('delete')}>
                        {t('delete')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <WidgetCreator open={open === 'edit'} onClose={close} widget={widget} />
            <ShareWidget widget={widget} open={open === 'share'} onClose={close} />
            <DeleteWidgetDialog widget={widget} open={open === 'delete'} onClose={close} />
        </div>
    );
}
